'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiSwaggerClientSide } from '@/shared/api';
import { KeycloakUser, Sample } from '@/sharedTypes/Api';
import { ArrowLeft, X, Plus, Trash2 } from 'lucide-react';
import toast from '@/components/ui/custom-toast';
import { useLanguageFromPath } from '@/hooks/useLanguageFromPath';

interface IGroupDetail {
  id: string | null;
  onBack: () => void;
}

interface GroupMember {
  sub: string;
  username: string;
  email?: string;
}

interface AssignedSample {
  _id: string;
  title: string;
  identifier: string;
}

interface GroupState {
  name: string;
  members: GroupMember[];
  assignedSampleIds: Set<string>;
}

const GroupDetail = ({ id, onBack }: IGroupDetail) => {
  const language = useLanguageFromPath();
  const isCreateMode = !id;

  // Loading states
  const [loading, setLoading] = useState(!isCreateMode);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initial state (for reset on cancel)
  const [initialState, setInitialState] = useState<GroupState>({
    name: '',
    members: [],
    assignedSampleIds: new Set(),
  });

  // Current editable state
  const [name, setName] = useState('');
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [assignedSamples, setAssignedSamples] = useState<AssignedSample[]>([]);
  const [samplesToAdd, setSamplesToAdd] = useState<Set<string>>(new Set());
  const [samplesToRemove, setSamplesToRemove] = useState<Set<string>>(new Set());

  // Search states
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchedUsers, setSearchedUsers] = useState<KeycloakUser[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);

  const [sampleSearchQuery, setSampleSearchQuery] = useState('');
  const [searchedSamples, setSearchedSamples] = useState<Sample[]>([]);
  const [isSearchingSamples, setIsSearchingSamples] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Fetch group data in edit mode
  const fetchGroupData = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      // Fetch group details
      const groupResponse = await apiSwaggerClientSide.admin.groupsDetail(id);
      if (groupResponse.data?.success && groupResponse.data.data) {
        const group = groupResponse.data.data;
        const groupMembers: GroupMember[] = (group.members || []).map((m) => ({
          sub: m.sub || '',
          username: m.username || '',
          email: m.email,
        }));

        setName(group.name || '');
        setMembers(groupMembers);

        // Fetch samples assigned to this group
        const samplesResponse = await apiSwaggerClientSide.admin.samplesList({
          group: id,
          pageSize: 1000, // Get all samples for this group
        });

        const samples: AssignedSample[] = [];
        if (samplesResponse.data?.success && samplesResponse.data.data?.samples) {
          samplesResponse.data.data.samples.forEach((s) => {
            samples.push({
              _id: s._id,
              title: s.metadata?.title?.[language] || s.metadata?.title?.en || s.identifier,
              identifier: s.identifier,
            });
          });
        }
        setAssignedSamples(samples);

        // Store initial state for cancel reset
        setInitialState({
          name: group.name || '',
          members: groupMembers,
          assignedSampleIds: new Set(samples.map((s) => s._id)),
        });
      }
    } catch (error) {
      console.error('Failed to fetch group:', error);
      toast({
        title: 'Error loading group',
        description: 'Failed to fetch group details. Please try again.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [id, language]);

  useEffect(() => {
    if (!isCreateMode) {
      fetchGroupData();
    }
  }, [isCreateMode, fetchGroupData]);

  // Debounced user search
  useEffect(() => {
    if (userSearchQuery.length < 2) {
      setSearchedUsers([]);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearchingUsers(true);
      try {
        const res = await apiSwaggerClientSide.admin.usersSearchList({ q: userSearchQuery });
        if (res.data?.success && res.data.data) {
          // Filter out users already in the group
          const memberSubs = new Set(members.map((m) => m.sub));
          setSearchedUsers(res.data.data.filter((u) => u.sub && !memberSubs.has(u.sub)));
        } else {
          setSearchedUsers([]);
        }
      } catch (error) {
        console.error('Error searching users:', error);
      } finally {
        setIsSearchingUsers(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [userSearchQuery, members]);

  // Debounced sample search
  useEffect(() => {
    if (sampleSearchQuery.length < 2) {
      setSearchedSamples([]);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsSearchingSamples(true);
      try {
        const res = await apiSwaggerClientSide.admin.samplesList({
          search: sampleSearchQuery,
          pageSize: 20,
        });
        if (res.data?.success && res.data.data?.samples) {
          // Filter out samples already assigned (unless marked for removal)
          const assignedIds = new Set(assignedSamples.map((s) => s._id));
          setSearchedSamples(
            res.data.data.samples.filter(
              (s) => !assignedIds.has(s._id) || samplesToRemove.has(s._id)
            )
          );
        } else {
          setSearchedSamples([]);
        }
      } catch (error) {
        console.error('Error searching samples:', error);
      } finally {
        setIsSearchingSamples(false);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [sampleSearchQuery, assignedSamples, samplesToRemove]);

  const handleAddUser = (user: KeycloakUser) => {
    if (!user.sub || !user.username) return;
    setMembers((prev) => [...prev, { sub: user.sub!, username: user.username!, email: user.email }]);
    setUserSearchQuery('');
    setSearchedUsers([]);
  };

  const handleRemoveUser = (sub: string) => {
    setMembers((prev) => prev.filter((m) => m.sub !== sub));
  };

  const handleAssignSample = (sample: Sample) => {
    const newSample: AssignedSample = {
      _id: sample._id,
      title: sample.metadata?.title?.[language] || sample.metadata?.title?.en || sample.identifier,
      identifier: sample.identifier,
    };

    // If it was previously assigned and marked for removal, just unmark it
    if (samplesToRemove.has(sample._id)) {
      setSamplesToRemove((prev) => {
        const next = new Set(prev);
        next.delete(sample._id);
        return next;
      });
    } else {
      // Add to samples to add
      setSamplesToAdd((prev) => new Set(prev).add(sample._id));
      setAssignedSamples((prev) => [...prev, newSample]);
    }

    setSampleSearchQuery('');
    setSearchedSamples([]);
  };

  const handleUnassignSample = (sampleId: string) => {
    // If it was newly added, just remove it from the add list
    if (samplesToAdd.has(sampleId)) {
      setSamplesToAdd((prev) => {
        const next = new Set(prev);
        next.delete(sampleId);
        return next;
      });
      setAssignedSamples((prev) => prev.filter((s) => s._id !== sampleId));
    } else {
      // Mark for removal
      setSamplesToRemove((prev) => new Set(prev).add(sampleId));
    }
  };

  const handleCancel = () => {
    // Reset to initial state
    setName(initialState.name);
    setMembers(initialState.members);
    setAssignedSamples(
      Array.from(initialState.assignedSampleIds).map((id) => {
        const existing = assignedSamples.find((s) => s._id === id);
        return existing || { _id: id, title: id, identifier: id };
      })
    );
    setSamplesToAdd(new Set());
    setSamplesToRemove(new Set());
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a group name.',
        type: 'error',
      });
      return;
    }

    setIsSaving(true);
    try {
      let groupId = id;

      if (isCreateMode) {
        // Create new group
        const createResponse = await apiSwaggerClientSide.admin.groupsCreate({
          name: name.trim(),
          members: members.map((m) => m.sub),
        });

        if (createResponse.data?.success && createResponse.data.data?._id) {
          groupId = createResponse.data.data._id;
          toast({
            title: 'Group created',
            description: `Group "${name}" has been created.`,
            type: 'success',
          });
        } else {
          throw new Error('Failed to create group');
        }
      } else {
        // Update existing group
        await apiSwaggerClientSide.admin.groupsPartialUpdate(id!, {
          name: name.trim(),
          members: members.map((m) => m.sub),
        });
      }

      // Update sample assignments
      if (groupId) {
        // Add group to new samples
        if (samplesToAdd.size > 0) {
          await apiSwaggerClientSide.admin.samplesBulkCreate({
            sampleIds: Array.from(samplesToAdd),
            groups: [groupId],
          });
        }

        // Remove group from samples
        if (samplesToRemove.size > 0) {
          await apiSwaggerClientSide.admin.samplesBulkCreate({
            sampleIds: Array.from(samplesToRemove),
            groupsToRemove: [groupId],
          });
        }
      }

      if (!isCreateMode) {
        toast({
          title: 'Group updated',
          description: `Group "${name}" has been updated.`,
          type: 'success',
        });
      }

      onBack();
    } catch (error) {
      console.error('Failed to save group:', error);
      toast({
        title: 'Error saving group',
        description: 'Failed to save the group. Please try again.',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setIsDeleting(true);
    try {
      const response = await apiSwaggerClientSide.admin.groupsDelete(id);
      if (response.data?.success) {
        toast({
          title: 'Group deleted',
          description: `Group "${name}" has been deleted.`,
          type: 'success',
        });
        onBack();
      }
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast({
        title: 'Error deleting group',
        description: 'Failed to delete the group. Please try again.',
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full gap-4">
        <Skeleton className="h-10 w-64" />
        <div className="flex gap-4 flex-1">
          <Skeleton className="flex-[4] h-full" />
          <Skeleton className="flex-[6] h-full" />
        </div>
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Get visible assigned samples (excluding those marked for removal)
  const visibleAssignedSamples = assignedSamples.filter((s) => !samplesToRemove.has(s._id));

  return (
    <div className="flex flex-col h-full" data-testid="group-detail-view">
      {/* Header with editable name */}
      <div className="flex items-center gap-4 mb-4 pb-4 border-b">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors border-0 bg-transparent cursor-pointer"
          data-testid="group-detail-back-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Input
          type="text"
          placeholder="Enter group name..."
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="text-lg font-semibold flex-1 max-w-md"
          data-testid="group-detail-name-input"
        />
      </div>

      {/* Two-column content */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* Left column - Manage Users (40%) */}
        <div className="flex-[4] flex flex-col border rounded-lg p-4 overflow-hidden">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Manage Users
          </h3>

          {/* User search */}
          <div className="mb-3">
            <Input
              type="text"
              placeholder="Search users to add... (min 2 chars)"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              data-testid="group-detail-user-search"
            />
            {/* Search results dropdown */}
            {(searchedUsers.length > 0 || isSearchingUsers) && (
              <div className="mt-1 border rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
                {isSearchingUsers ? (
                  <div className="p-2 text-sm text-muted-foreground">Searching...</div>
                ) : (
                  searchedUsers.map((user) => (
                    <button
                      key={user.sub}
                      onClick={() => handleAddUser(user)}
                      className="w-full text-left p-2 hover:bg-gray-100 border-0 bg-transparent cursor-pointer text-sm"
                    >
                      <span className="font-medium">{user.username}</span>
                      {user.email && (
                        <span className="text-muted-foreground ml-2">({user.email})</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Current members list */}
          <div className="flex-1 overflow-y-auto">
            {members.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">
                No members in this group. Search above to add users.
              </div>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.sub}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                    data-testid={`group-member-${member.sub}`}
                  >
                    <div className="text-sm">
                      <span className="font-medium">{member.username}</span>
                      {member.email && (
                        <span className="text-muted-foreground ml-2">({member.email})</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveUser(member.sub)}
                      className="p-1 hover:bg-red-100 rounded text-red-600 border-0 bg-transparent cursor-pointer"
                      data-testid={`group-member-remove-${member.sub}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column - Assign Pieces (60%) */}
        <div className="flex-[6] flex flex-col border rounded-lg p-4 overflow-hidden">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
            Assign Pieces
          </h3>

          {/* Sample search */}
          <div className="mb-3">
            <Input
              type="text"
              placeholder="Search samples to assign... (min 2 chars)"
              value={sampleSearchQuery}
              onChange={(e) => setSampleSearchQuery(e.target.value)}
              data-testid="group-detail-sample-search"
            />
            {/* Search results dropdown */}
            {(searchedSamples.length > 0 || isSearchingSamples) && (
              <div className="mt-1 border rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
                {isSearchingSamples ? (
                  <div className="p-2 text-sm text-muted-foreground">Searching...</div>
                ) : (
                  searchedSamples.map((sample) => (
                    <button
                      key={sample._id}
                      onClick={() => handleAssignSample(sample)}
                      className="w-full text-left p-2 hover:bg-gray-100 border-0 bg-transparent cursor-pointer text-sm flex items-center justify-between"
                    >
                      <span>
                        {sample.metadata?.title?.[language] ||
                          sample.metadata?.title?.en ||
                          sample.identifier}
                      </span>
                      <Plus className="h-4 w-4 text-green-600" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Assigned samples list */}
          <div className="flex-1 overflow-y-auto">
            {visibleAssignedSamples.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-4">
                No samples assigned to this group. Search above to assign samples.
              </div>
            ) : (
              <div className="space-y-2">
                {visibleAssignedSamples.map((sample) => {
                  const isNewlyAdded = samplesToAdd.has(sample._id);
                  return (
                    <div
                      key={sample._id}
                      className={`flex items-center justify-between p-2 rounded-md ${
                        isNewlyAdded ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                      }`}
                    >
                      <div className="text-sm">
                        <span className="font-medium">{sample.title}</span>
                        {isNewlyAdded && (
                          <span className="ml-2 text-xs text-green-600">(new)</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnassignSample(sample._id)}
                        className="p-1 hover:bg-red-100 rounded text-red-600 border-0 bg-transparent cursor-pointer"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between items-center pt-4 mt-4 border-t">
        <div>
          {!isCreateMode && (
            <ButtonShadcn
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={isSaving || isDeleting}
              data-testid="group-detail-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Group
            </ButtonShadcn>
          )}
        </div>
        <div className="flex gap-2">
          <ButtonShadcn variant="outline" onClick={handleCancel} disabled={isSaving || isDeleting} title='Revert to initial state' data-testid="group-detail-cancel-btn">
            Cancel
          </ButtonShadcn>
          <ButtonShadcn variant="default" onClick={handleSave} disabled={isSaving || isDeleting} data-testid="group-detail-save-btn">
            {isSaving ? 'Saving...' : isCreateMode ? 'Create Group' : 'Save Changes'}
          </ButtonShadcn>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Group</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the group "{name}"? This will remove the group from
              all samples that have it assigned. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <ButtonShadcn
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
              data-testid="group-detail-delete-cancel-btn"
            >
              Cancel
            </ButtonShadcn>
            <ButtonShadcn
              variant="default"
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDelete}
              disabled={isDeleting}
              data-testid="group-detail-delete-confirm-btn"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </ButtonShadcn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupDetail;
